// FloorExpandedView — single-floor command-center view.
//
// Composition:
//   - Background: AI-parsed layout geometry rendered as SVG (walls /
//     rooms / doors / corridors / entry_points / labels) from the
//     current published version's normalized_json. The raw uploaded
//     image is intentionally NOT shown — operators have explicitly
//     asked for "AI 處理過的結果" only, never the raw source.
//   - 5 toggleable overlay layers per spec §9:
//       1. LocationLayer  — markers (location / camera / aed / exit)
//       2. RouteLayer       — polylines + sequence numbers
//       3. PatrolLayer      — pulsing rings on completed locations
//       4. IncidentLayer    — pin per active incident with severity colour
//       5. NotificationLayer— count badge top-right
//   - Right side: floor metadata + layer toggles + active incidents list.
//   - Back button (ESC / 返回) collapses back to the stack.

import React, { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ChevronLeft, AlertTriangle, Layers as LayersIcon, MapPin, Route as RouteIcon,
  Bell, Shield, Box, Map as MapIcon, Loader2,
} from "lucide-react";
import {
  EMPTY_LAYOUT,
  type CommandViewResponse,
  type NormalizedLayoutDto,
} from "../lib/floorStackApi.js";
import LocationLayer from "./layers/LocationLayer.js";
import RouteLayer, { ROUTE_PALETTE } from "./layers/RouteLayer.js";
import PatrolLayer from "./layers/PatrolLayer.js";
import IncidentLayer from "./layers/IncidentLayer.js";
import NotificationLayer from "./layers/NotificationLayer.js";
import LayoutGeometry from "./LayoutGeometry.js";

// 3D viewer is a separate chunk — only loaded when the operator clicks
// the "3D" toggle. three.js + @react-three/fiber + @react-three/drei
// are non-trivial; lazy keeps the 2D-only sessions cheap.
const FloorLayout3D = lazy(() => import("./three/FloorLayout3D.js"));

interface Props {
  data: CommandViewResponse;
  tenantId: string | null;
  onBack: () => void;
  reduced: boolean;
}

const SEVERITY_BADGE: Record<string, string> = {
  info: "bg-bg-raised text-text-secondary",
  low: "bg-amber-500/15 text-amber-400",
  med: "bg-orange-500/15 text-orange-400",
  high: "bg-rose-500/15 text-rose-400",
  critical: "bg-red-500/20 text-red-300 border border-red-400/40",
};

export default function FloorExpandedView({ data, tenantId: _tenantId, onBack, reduced }: Props): React.ReactElement {
  const [showLocations, setShowLocations] = useState(true);
  const [showRoutes, setShowRoutes] = useState(true);
  const [showPatrol, setShowPatrol] = useState(true);
  const [showIncidents, setShowIncidents] = useState(true);
  const [showNotification, setShowNotification] = useState(true);
  // Per-route on/off. Initialised lazily from data.routes (all visible);
  // kept in sync when the routes set changes between command-view polls
  // — newly-added routes default to visible, removed routes are pruned.
  const [routeVisibility, setRouteVisibility] = useState<Record<string, boolean>>(
    () => Object.fromEntries(data.routes.map((r) => [r.id, true])),
  );
  useEffect(() => {
    setRouteVisibility((prev) => {
      const next: Record<string, boolean> = {};
      for (const r of data.routes) {
        next[r.id] = prev[r.id] ?? true;
      }
      return next;
    });
  }, [data.routes]);
  const [viewMode, setViewMode] = useState<"2d" | "3d">("2d");
  const [contextLostMsg, setContextLostMsg] = useState<string | null>(null);
  // r3f's Canvas dispose path calls forceContextLoss() during unmount,
  // which fires our onContextLost — same as a real GPU drop. Track
  // operator-initiated closes so we don't toast on those.
  const userClosed3dRef = useRef(false);
  const closeTo2D = useCallback((): void => {
    userClosed3dRef.current = true;
    setViewMode("2d");
  }, []);

  // ESC key → back (in 3D mode it instead drops back to 2D first; second
  // ESC then closes the expanded view).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (viewMode === "3d") closeTo2D();
      else onBack();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onBack, viewMode, closeTo2D]);

  const ver = data.layout?.current_version ?? null;
  // Parse the published version's normalized_json once. If parsing
  // fails (legacy versions without AI output, malformed JSON, etc.)
  // we fall through to the empty-layout placeholder below — never
  // crash the command view, never fall back to the raw image.
  const layout = useMemo<NormalizedLayoutDto | null>(() => {
    if (!ver?.normalized_json) return null;
    try {
      const parsed = JSON.parse(ver.normalized_json) as NormalizedLayoutDto;
      if (!parsed || !parsed.image_size) return null;
      return { ...EMPTY_LAYOUT, ...parsed };
    } catch {
      return null;
    }
  }, [ver]);
  const viewBox = layout
    ? `0 0 ${layout.image_size.width} ${layout.image_size.height}`
    : "0 0 1000 700";
  const snap = data.latest_snapshot;

  // Map of location id → { code, label } for the 3D Markers overlay
  // (FloorLayout3D's tooltip layer wants this same shape as the editor).
  // Source: routes[].locations[].location inlined by /command-view.
  const locationLookup = useMemo(() => {
    const m = new Map<string, { code: string; label: string }>();
    for (const r of data.routes) {
      for (const rc of r.locations) {
        if (rc.location?.id) m.set(rc.location.id, { code: rc.location.code, label: rc.location.label });
      }
    }
    return m;
  }, [data.routes]);

  // The expanded view is read-only for marker placement (this is the
  // command-center role; geometry edits live on the layout editor page).
  // Surface the no-op explicitly so 3D Markers render the drag handles
  // but a release just snaps back instead of silently failing.
  const onMarkerMove3D = useCallback((_id: string, _pos: { x: number; y: number }) => {
    setContextLostMsg("地點位置只能在「樓層圖資編輯」頁修改。");
    setTimeout(() => setContextLostMsg(null), 3000);
  }, []);

  return (
    <div
      className="floor-expanded grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4 animate-in fade-in"
      data-floor-expanded={data.floor.id}
      style={{
        animation: reduced ? "none" : "floor-expanded-in 380ms cubic-bezier(0.22,1,0.36,1)",
      }}
    >
      <style>
        {`@keyframes floor-expanded-in {
            from { opacity: 0; transform: scale(0.96); }
            to   { opacity: 1; transform: scale(1); }
          }`}
      </style>
      {/* ── Canvas pane ─────────────────────────────────────────────── */}
      <section className="rounded border border-border bg-bg-surface p-3">
        <header className="mb-3 flex items-center justify-between gap-2 flex-wrap">
          <button onClick={onBack} className="btn inline-flex items-center gap-1.5">
            <ChevronLeft className="h-4 w-4" />
            返回預覽
          </button>
          <div className="flex items-center gap-2">
            {/* 2D / 3D toggle. 3D is read-only for both geometry AND
                marker positions on this page (this is the command
                center role; edits live on the layout editor). */}
            <button
              type="button"
              className={`btn-ghost text-xs ${viewMode === "3d" ? "bg-accent/15 text-accent border border-accent/40" : ""}`}
              onClick={() => {
                if (viewMode === "3d") closeTo2D();
                else setViewMode("3d");
              }}
              disabled={!layout}
              title={viewMode === "2d"
                ? "切換成 3D 立體檢視（牆 / 地點位置在此頁不可編輯）"
                : "切回 2D 平面檢視"}
              data-testid="floor-preview-view-toggle"
            >
              {viewMode === "2d" ? <><Box className="h-3.5 w-3.5" /> 3D</> : <><MapIcon className="h-3.5 w-3.5" /> 2D</>}
            </button>
            <div className="text-sm text-text-secondary flex items-center gap-3">
              <span className="font-mono text-text-primary">{data.floor.floor_name}</span>
              <span className="text-text-muted">·</span>
              <span>{data.building.name}</span>
              <span className="text-text-muted">·</span>
              <span>{data.site.name}</span>
              <a
                href={`/dashboard/sites/${data.site.id}/buildings/${data.building.id}/floors/${data.floor.id}/layout`}
                className="ml-2 inline-flex items-center gap-1 text-xs text-accent hover:underline"
                title="上傳新平面圖、用 ChatGPT 自動辨識牆 / 房 / 門 / 走道，或手動編輯"
              >
                編輯樓層平面圖
              </a>
            </div>
          </div>
        </header>
        {contextLostMsg && (
          <div className="mb-2 rounded border border-warning/40 bg-warning/5 px-3 py-2 text-xs text-warning inline-flex items-center gap-2">
            <AlertTriangle className="h-3.5 w-3.5" /> {contextLostMsg}
          </div>
        )}
        {layout ? (
          viewMode === "3d" ? (
            <div className="overflow-hidden rounded border border-border bg-bg-base">
              <Suspense
                fallback={
                  <div className="grid place-items-center bg-bg-deep w-full h-[60vh]">
                    <div className="text-center text-sm text-text-secondary">
                      <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2 text-accent" />
                      載入 3D 視圖中⋯
                    </div>
                  </div>
                }
              >
                <FloorLayout3D
                  layout={layout}
                  markers={data.markers}
                  locationLookup={locationLookup}
                  onMarkerMove={onMarkerMove3D}
                  onContextLost={() => {
                    if (userClosed3dRef.current) {
                      userClosed3dRef.current = false;
                      return;
                    }
                    setViewMode("2d");
                    setContextLostMsg("3D 視圖暫時不可用（瀏覽器 WebGL 失效），已切回 2D。");
                    setTimeout(() => setContextLostMsg(null), 4000);
                  }}
                />
              </Suspense>
            </div>
          ) : (
            <div className="overflow-hidden rounded border border-border bg-bg-base">
              <svg viewBox={viewBox} preserveAspectRatio="xMidYMid meet" className="w-full" data-testid="command-canvas">
                {/* Faint grid for orientation. */}
                <defs>
                  <pattern id="cmd-grid" width={50} height={50} patternUnits="userSpaceOnUse">
                    <path d="M 50 0 L 0 0 0 50" fill="none" stroke="rgba(0, 212, 255, 0.05)" strokeWidth={1} />
                  </pattern>
                </defs>
                <rect x={0} y={0} width={layout.image_size.width} height={layout.image_size.height} fill="url(#cmd-grid)" />
                {/* AI-parsed layout — replaces the raw R2 image. */}
                <LayoutGeometry layout={layout} opacity={0.9} />
                <LocationLayer  data={data} visible={showLocations} />
                <RouteLayer       data={data} visible={showRoutes} routeVisibility={routeVisibility} />
                <PatrolLayer      data={data} visible={showPatrol} />
                <IncidentLayer    data={data} visible={showIncidents} />
                <NotificationLayer data={data} visible={showNotification} />
              </svg>
            </div>
          )
        ) : (
          <div className="flex flex-col items-center justify-center rounded border border-dashed border-border bg-bg-base px-6 py-16 text-center">
            <LayersIcon className="mb-3 h-10 w-10 text-text-secondary" />
            <h3 className="mb-1 text-base font-semibold text-text-primary">這層樓尚未有 AI 解析結果</h3>
            <p className="text-sm text-text-secondary">回到預覽後到「樓層圖資編輯」上傳一張底圖，等 AI 解析完即可在此呈現。</p>
          </div>
        )}
      </section>

      {/* ── Side pane ────────────────────────────────────────────────── */}
      <aside className="flex flex-col gap-3">
        {/* Snapshot summary */}
        <div className="rounded border border-border bg-bg-surface p-3" data-testid="snapshot-summary">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-text-muted">即時狀態</h3>
          {snap ? (
            <dl className="grid grid-cols-2 gap-y-2 text-sm">
              <dt className="text-text-secondary">完成率</dt>
              <dd className="font-mono tabular-nums text-text-primary">
                {snap.location_total > 0 ? `${Math.round(snap.completion_rate * 100)}%` : "—"}
                <span className="ml-1 text-xs text-text-muted">({snap.completed}/{snap.location_total})</span>
              </dd>
              <dt className="text-text-secondary">超時</dt>
              <dd className="font-mono tabular-nums text-amber-400">{snap.overdue}</dd>
              <dt className="text-text-secondary">未結警示</dt>
              <dd className="font-mono tabular-nums text-amber-400">{snap.incident_warning_count}</dd>
              <dt className="text-text-secondary">未結危急</dt>
              <dd className="font-mono tabular-nums text-rose-400">{snap.incident_critical_count}</dd>
              <dt className="text-text-secondary">在勤</dt>
              <dd className="font-mono tabular-nums text-text-primary">{snap.online_officers}</dd>
              <dt className="text-text-secondary">最後巡邏</dt>
              <dd className="text-xs text-text-secondary">{snap.last_patrol_at ?? "—"}</dd>
            </dl>
          ) : (
            <p className="text-xs text-text-muted">尚未有快照（每 5 分鐘更新一次）。</p>
          )}
        </div>

        {/* Layer toggles */}
        <div className="rounded border border-border bg-bg-surface p-3" data-testid="layer-toggles">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-text-muted">圖層</h3>
          <ul className="space-y-1.5 text-sm">
            <Toggle icon={MapPin}        label="地點"     colour="#3ec1ff" value={showLocations} onChange={setShowLocations} />
            <Toggle icon={RouteIcon}     label="巡邏路線"   colour="#ffa743" value={showRoutes} onChange={setShowRoutes} />
            {showRoutes && data.routes.length > 0 && (
              <li>
                <ul className="ml-6 mt-0.5 mb-1 space-y-0.5 border-l border-border pl-2">
                  {data.routes.map((route, idx) => {
                    const colour = ROUTE_PALETTE[idx % ROUTE_PALETTE.length];
                    const value = routeVisibility[route.id] ?? true;
                    return (
                      <RouteSubToggle
                        key={route.id}
                        label={route.name}
                        colour={colour}
                        value={value}
                        onChange={(v) => setRouteVisibility((s) => ({ ...s, [route.id]: v }))}
                      />
                    );
                  })}
                </ul>
              </li>
            )}
            <Toggle icon={Shield}        label="即時巡邏" colour="#3ec1ff" value={showPatrol} onChange={setShowPatrol} />
            <Toggle icon={AlertTriangle} label="事件"       colour="#ff5b6d" value={showIncidents} onChange={setShowIncidents} />
            <Toggle icon={Bell}          label="通知計數" colour="#ffb800" value={showNotification} onChange={setShowNotification} />
          </ul>
        </div>

        {/* Active incidents list */}
        <div className="rounded border border-border bg-bg-surface p-3 max-h-72 overflow-y-auto">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-text-muted">未結事件 ({data.active_incidents.length})</h3>
          {data.active_incidents.length === 0 ? (
            <p className="text-xs text-text-muted">這層目前沒有未結 incident。</p>
          ) : (
            <ul className="space-y-2">
              {data.active_incidents.map((inc) => (
                <li key={inc.id} className="rounded border border-border bg-bg-base p-2">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className={`px-2 py-0.5 text-[10px] rounded ${SEVERITY_BADGE[inc.severity] ?? "bg-bg-raised"}`}>
                      {inc.severity.toUpperCase()}
                    </span>
                    <span className="text-[10px] text-text-muted">{inc.created_at.slice(0, 16).replace("T", " ")}</span>
                  </div>
                  <p className="text-sm text-text-primary truncate">{inc.title}</p>
                  <p className="text-xs text-text-secondary line-clamp-2">{inc.body}</p>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Routes summary */}
        <div className="rounded border border-border bg-bg-surface p-3 max-h-60 overflow-y-auto">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-text-muted">經過此樓的路線</h3>
          {data.routes.length === 0 ? (
            <p className="text-xs text-text-muted">無相關 route。</p>
          ) : (
            <ul className="space-y-1.5">
              {data.routes.map((r) => (
                <li key={r.id} className="text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-text-primary truncate">{r.name}</span>
                    <span className="text-xs text-text-muted">
                      {r.locations.length} 點 / {r.cadence_minutes ?? "?"} min
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </aside>
    </div>
  );
}

interface ToggleProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  colour: string;
  value: boolean;
  onChange: (v: boolean) => void;
}

function Toggle({ icon: Icon, label, colour, value, onChange }: ToggleProps): React.ReactElement {
  return (
    <li>
      <button
        type="button"
        onClick={() => onChange(!value)}
        className="w-full flex items-center justify-between rounded px-2 py-1.5 hover:bg-bg-hover transition-colors"
        aria-pressed={value}
        data-toggle-label={label}
      >
        <span className="flex items-center gap-2">
          <Icon className="h-3.5 w-3.5" />
          <span className="text-sm">{label}</span>
        </span>
        <span
          className="inline-flex h-4 w-7 items-center rounded-full transition-colors"
          style={{ background: value ? colour : "rgba(155,155,161,0.25)" }}
        >
          <span
            className="inline-block h-3 w-3 transform rounded-full bg-bg-base transition-transform"
            style={{ transform: value ? "translateX(14px)" : "translateX(2px)" }}
          />
        </span>
      </button>
    </li>
  );
}

// Compact sub-row for a single patrol route under the 巡邏路線 master
// toggle. Carries the route's colour swatch (matching what RouteLayer
// renders on the SVG) + the route's name + a tiny toggle pill. Same
// click target / aria-pressed semantics as <Toggle> but visually
// indented and slightly denser so the parent toggle row stays
// readable.
interface RouteSubToggleProps {
  label: string;
  colour: string;
  value: boolean;
  onChange: (next: boolean) => void;
}
function RouteSubToggle({ label, colour, value, onChange }: RouteSubToggleProps): React.ReactElement {
  return (
    <li>
      <button
        type="button"
        onClick={() => onChange(!value)}
        className="w-full flex items-center justify-between rounded px-2 py-1 hover:bg-bg-hover transition-colors"
        aria-pressed={value}
        data-route-toggle={label}
      >
        <span className="flex items-center gap-1.5 min-w-0">
          <span
            className="inline-block h-2 w-2 rounded-full flex-shrink-0"
            style={{ background: colour, opacity: value ? 1 : 0.35 }}
          />
          <span className={`text-xs truncate ${value ? "text-text-primary" : "text-text-muted"}`}>{label}</span>
        </span>
        <span
          className="inline-flex h-3 w-6 items-center rounded-full transition-colors flex-shrink-0"
          style={{ background: value ? colour : "rgba(155,155,161,0.25)" }}
        >
          <span
            className="inline-block h-2 w-2 transform rounded-full bg-bg-base transition-transform"
            style={{ transform: value ? "translateX(13px)" : "translateX(2px)" }}
          />
        </span>
      </button>
    </li>
  );
}
